/**
 * Shared handlers for the recent-chats sidebar (ChatPage) and dropdown
 * (ChatPanel). Centralizes session select / new / rename / delete + undo
 * so both surfaces behave identically.
 *
 * AI Chat V1.1 Item 4 (DR #43, 2026-05-03).
 */
import { useCallback } from 'react';

import { api } from '../../../api/client';
import {
  useDeleteSession,
  useRenameSession,
  useRestoreSession,
} from '../../../api/hooks/useChatSessions';
import { useChatStore, type ChatMessage } from '../../../stores/chatStore';
import { useUIStore } from '../../../stores/uiStore';

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

interface Args {
  cancel: () => void;
}

export function useChatHistoryActions({ cancel }: Args) {
  const sessionId = useChatStore((s) => s.sessionId);
  const sessions = useChatStore((s) => s.sessions);
  const setMessages = useChatStore((s) => s.setMessages);
  const setSessionId = useChatStore((s) => s.setSessionId);
  const setDealContext = useChatStore((s) => s.setDealContext);
  const clearConversationState = useChatStore((s) => s.clearConversationState);
  const setLoadingHistory = useChatStore((s) => s.setLoadingHistory);
  const setUploadPrompt = useChatStore((s) => s.setUploadPrompt);
  const setDealCreationPrompt = useChatStore((s) => s.setDealCreationPrompt);
  const setDealSelectionPrompt = useChatStore((s) => s.setDealSelectionPrompt);
  const setTermSheetSelectionPrompt = useChatStore(
    (s) => s.setTermSheetSelectionPrompt,
  );

  const showToast = useUIStore((s) => s.showToast);
  const showToastWithAction = useUIStore((s) => s.showToastWithAction);

  const renameMutation = useRenameSession();
  const deleteMutation = useDeleteSession();
  const restoreMutation = useRestoreSession();

  const handleNewChat = useCallback(() => {
    cancel();
    // Single canonical clear — nulls every conversation-scoped field so the
    // fresh chat carries no context from the previous one. Preserves
    // advisorContext (page-scoped).
    clearConversationState();
  }, [cancel, clearConversationState]);

  const handleSelectSession = useCallback(
    async (selectedSessionId: string) => {
      if (selectedSessionId === sessionId) {return;}
      const session = sessions.find((s) => s.id === selectedSessionId);
      if (!session) {return;}

      cancel();
      // Canonical clear first — drops the previous conversation's messages,
      // deal context, resolved-deal card, navigation chip, and all UI-control
      // prompts in one shot. Then set this session's own values. The only
      // prompt that should reappear is one we re-hydrate from this session's
      // last assistant turn (below).
      clearConversationState();
      setLoadingHistory(true);
      setSessionId(selectedSessionId);

      if (session.dealId !== null) {
        setDealContext({
          deal_id: session.dealId,
          deal_name: session.dealName ?? undefined,
        });
      }

      try {
        const rows = await api.chat.getSessionMessages(selectedSessionId);
        const restored: ChatMessage[] = rows.map((r) => ({
          id: newId(),
          role: r.role,
          content: r.content,
          // V1.5 follow-up — tool_results are persisted as JSONB so inline
          // cards (deal cards, benchmark rows, etc.) survive a reload.
          // Backend returns null for user turns / no-tool assistant turns;
          // we hand the array straight through to the registry renderer.
          toolResults: r.tool_results ?? undefined,
          // Citations Phase 2 — citations persisted on the assistant turn
          // surface again on resume so [N] markers + footnote list survive
          // a session switch. Null on user turns / pre-Phase-2 rows.
          citations: (r.citations ?? undefined) as ChatMessage['citations'],
        }));
        setMessages(restored);

        // Re-hydrate UI-control prompts (upload card, deal-creation card,
        // deal/term-sheet pickers) from the restored thread. Without this,
        // an `awaiting_*` turn loses its card on refresh — the marker is
        // persisted in tool_results JSONB but the transient store prompt
        // is not.
        //
        // Freshness gate: only re-hydrate when the conversation ENDS on
        // the awaiting marker (last message is the assistant turn that
        // emitted it AND no user turn has happened since). A pending
        // prompt that the user has already moved past should stay dead.
        const last = restored[restored.length - 1];
        if (last && last.role === 'assistant' && last.toolResults?.length) {
          for (const tr of last.toolResults) {
            const result = (tr.result ?? {}) as Record<string, unknown>;
            if (
              tr.tool_name === 'request_upload' &&
              result.status === 'awaiting_upload' &&
              typeof result.deal_id === 'number'
            ) {
              setUploadPrompt({
                deal_id: result.deal_id,
                label:
                  typeof result.label === 'string' && result.label
                    ? result.label
                    : 'Upload your term sheet',
                reason:
                  typeof result.reason === 'string' ? result.reason : undefined,
              });
            } else if (
              tr.tool_name === 'request_deal_creation' &&
              result.status === 'awaiting_deal_creation'
            ) {
              // T-22 telemetry decision — prefill is intentionally NOT
              // persisted in the marker, so the rehydrated card opens
              // empty. The user fills it in manually.
              setDealCreationPrompt({ prefill: {} });
            } else if (
              tr.tool_name === 'request_deal_selection' &&
              result.status === 'awaiting_deal_selection' &&
              Array.isArray(result.deals)
            ) {
              setDealSelectionPrompt({
                deals: result.deals as never,
                reason:
                  typeof result.reason === 'string' ? result.reason : undefined,
              });
            } else if (
              tr.tool_name === 'request_term_sheet_selection' &&
              result.status === 'awaiting_term_sheet_selection' &&
              typeof result.deal_id === 'number' &&
              Array.isArray(result.term_sheets)
            ) {
              setTermSheetSelectionPrompt({
                deal_id: result.deal_id,
                term_sheets: result.term_sheets as never,
                reason:
                  typeof result.reason === 'string' ? result.reason : undefined,
              });
            }
          }
        }
      } catch {
        showToast('Could not load conversation.', 'error');
      } finally {
        setLoadingHistory(false);
      }
    },
    [
      sessionId,
      sessions,
      cancel,
      clearConversationState,
      setLoadingHistory,
      setMessages,
      setSessionId,
      setDealContext,
      showToast,
      setUploadPrompt,
      setDealCreationPrompt,
      setDealSelectionPrompt,
      setTermSheetSelectionPrompt,
    ]
  );

  const handleRenameSession = useCallback(
    (id: string, title: string) => {
      renameMutation.mutate({ sessionId: id, title });
    },
    [renameMutation]
  );

  const handleDeleteSession = useCallback(
    (id: string) => {
      const wasActive = id === sessionId;
      deleteMutation.mutate(id);
      showToastWithAction('Chat deleted.', 'info', {
        label: 'Undo',
        onClick: () => {
          restoreMutation.mutate(id);
        },
      });
      if (wasActive) {
        const next = sessions.find((s) => s.id !== id);
        if (next) {
          handleSelectSession(next.id);
        } else {
          handleNewChat();
        }
      }
    },
    [
      sessionId,
      sessions,
      deleteMutation,
      restoreMutation,
      showToastWithAction,
      handleSelectSession,
      handleNewChat,
    ]
  );

  return {
    handleNewChat,
    handleSelectSession,
    handleRenameSession,
    handleDeleteSession,
  };
}
